import * as dates from "react-big-calendar/lib/utils/dates";

export interface Accessors<T> {
  /** Returns the start date/time of the event */
  start: (event: T) => Date;
  /** Returns the end date/time of the event */
  end: (event: T) => Date;
  /** Returns whether the event is all day */
  allDay: (event: T) => boolean;
}

/**
 * Sorts two events according to:
 * 1. Start day (earlier first)
 * 2. Multi-day events before shorter events
 * 3. All-day events before timed events
 * 4. Start time (earlier first)
 *
 * @param evtA - First event
 * @param evtB - Second event
 * @param accessors - Accessor functions for start/end/allDay
 * @returns A negative number if evtA < evtB, positive if evtA > evtB, or 0 if equal
 */
export function sortEvents<T>(
  evtA: T,
  evtB: T,
  accessors: Accessors<T>,
): number {
  // 1) Compare by start-of-day
  const dayA = dates.startOf(accessors.start(evtA), "day").valueOf();
  const dayB = dates.startOf(accessors.start(evtB), "day").valueOf();
  const startSort = dayA - dayB;

  // 2) Compare by duration in days (multi-day events first)
  const durA = dates.diff(
    accessors.start(evtA),
    dates.ceil(accessors.end(evtA), "day"),
    "day",
  );
  const durB = dates.diff(
    accessors.start(evtB),
    dates.ceil(accessors.end(evtB), "day"),
    "day",
  );
  const durationSort = Math.max(durB, 1) - Math.max(durA, 1);

  // 3) All-day events before timed events
  const allDayA = Number(accessors.allDay(evtA));
  const allDayB = Number(accessors.allDay(evtB));
  const allDaySort = allDayB - allDayA;

  // 4) Finally, by start timestamp
  const timeSort =
    accessors.start(evtA).valueOf() - accessors.start(evtB).valueOf();

  return startSort || durationSort || allDaySort || timeSort;
}

export default sortEvents;
